import { makeCustomDiscountId } from 'components/Sell/utils/common'
import { isOpenItemId } from 'constant/magicNumber'
import {
    AssignTableContract,
    DiscountContract,
    DiscountLevel,
    DiscountType,
    ItemType,
    KitchenPrintStatus,
    ModifierGroupContract,
    OrderDetailContract,
    OrderDiscountContract,
    OrderDiscountType,
    OrderInventoryStatus,
    OrderLineContract,
    OrderLineKdsStatus,
    OrderLineLookupContract,
    OrderLineStatus,
    OrderStatus,
    POSVariantGroup,
    VoidCompReasonModel,
    VoidCompReasonType,
} from 'models'
import {
    ComboViewModel,
    DiscountContractWithCustom,
    EntityModifierViewModel,
    ItemViewModel,
    ModifierOptionLookup,
    OrderItemViewModel,
    PriceListData,
    UsedItem,
    UsedModifier,
} from 'store/models/sellModels'
import { buildCombo } from './buildCombo'
import { buildUsedVariants } from './buildUsedVariants'
import { buildUsedModifiers } from './buildUsedModifiers'
import { OPEN_ITEM_ID } from 'constant/magicNumber'
import { TOldSaleData } from 'views/Sale/hooks/useSaleSelectReducer/useSaleSelectReducer'
import dayjs from 'dayjs'
import { MENUSECTION_ALL_ITEMS } from 'views/Sale/utils/POSData/defaultPOSData'
import Decimal from 'decimal.js'
import { round } from 'views/Sale/utils/orderCalculation/common'

/**
 * convert order line to orderItemViewModel for display but unable use the result for sale process
 */
function buildMockOrderLineToOrderItems(
    orderLines: OrderLineLookupContract[] | OrderLineContract[],
) {
    return buildOrderItems(orderLines, {})
}

function buildOrderItems(
    items: OrderLineLookupContract[],
    deps?: {
        itemsMap?: { [key: string]: ItemViewModel }
        variantGroupsMap?: { [variantGroupID: string]: POSVariantGroup }
        modifierList?: EntityModifierViewModel[]
        modifierOptionsMap?: ModifierOptionLookup
        comboChoicesMap?: { [key: string]: ComboViewModel[] }
        discounts?: DiscountContract[]
        priceListData?: PriceListData
    },
): {
    orderItems: OrderItemViewModel[]
    removedData: TOldSaleData
} {
    const {
        itemsMap = {},
        variantGroupsMap = {},
        modifierList = [],
        modifierOptionsMap = {},
        comboChoicesMap = {},
        discounts: discountList = [],
        priceListData = {
            basePriceList: {},
            menuPriceListMap: {},
        },
    } = deps ?? {}
    let oldModifierOptionMaps: ModifierOptionLookup = {}
    const oldModifierGroupMaps: {
        [modifierGroupId: string]: ModifierGroupContract
    } = {}
    let oldModifierList: EntityModifierViewModel[] = []
    let oldComboMaps: {
        [itemId: string]: ComboViewModel[]
    } = {}
    let oldVariantGroupMaps: {
        [variantGroupId: string]: POSVariantGroup
    } = {}
    let oldItemsMap: {
        [itemId: string]: ItemViewModel
    } = {}

    const oItems: OrderItemViewModel[] = items.map<OrderItemViewModel>(
        (itemLine) => {
            const isCombo =
                itemLine.subOrderLines && itemLine.subOrderLines.length > 0
            let item: ItemViewModel | undefined
            let usedVariants: { [key: string]: string } = {}
            let usedModifiers: { [key: string]: UsedModifier[] } = {}
            let usedItems: { [key: string]: UsedItem } = {}
            if (isOpenItemId(itemLine.itemID)) {
                item = {
                    itemId: OPEN_ITEM_ID,
                    baseItemId: undefined,
                    name: itemLine.displayName || 'Open Item',
                    price: 0,
                    isTaxInclusive: itemLine.isTaxInclusive!,
                    type: ItemType.Miscellaneous,
                    variantGroups: [],
                    variations: [],
                    isAvailableForSelection: true,
                    combinationIds: [],
                    categoryId: '',
                    displayName: itemLine.displayName!,
                    imageUrl: '',
                    brandID: '',
                    enableTrackInventory: false,
                    inMenuSections: {},
                    isOutOfStock: false,
                    depositAndCreditItems: [],
                    overrideCategoryModifierSettings: [],
                }
            } else if (!isCombo) {
                const usedVars = buildUsedVariants(
                    itemLine,
                    itemsMap,
                    variantGroupsMap,
                    false,
                    oldItemsMap,
                )
                const useMod = buildUsedModifiers(
                    itemLine,
                    modifierOptionsMap,
                    modifierList,
                    priceListData,
                )

                item = usedVars.item
                usedVariants = usedVars.usedVariants
                usedModifiers = useMod.usedModifiers

                oldModifierList = [
                    ...oldModifierList,
                    ...useMod.oldModifierList,
                ]
                oldModifierOptionMaps = {
                    ...oldModifierOptionMaps,
                    ...useMod.oldModifierOptionMaps,
                }
                oldVariantGroupMaps = {
                    ...oldVariantGroupMaps,
                    ...usedVars.oldVariantGroupMaps,
                }
            } else {
                const comboItem = buildCombo(
                    itemLine,
                    itemsMap,
                    modifierOptionsMap,
                    modifierList,
                    comboChoicesMap,
                    variantGroupsMap,
                    priceListData,
                )
                item = comboItem.orderItem
                usedItems = comboItem.usedItems
                oldComboMaps = {
                    ...oldComboMaps,
                    ...comboItem.oldComboMap,
                }
                oldModifierOptionMaps = {
                    ...oldModifierOptionMaps,
                    ...comboItem.oldModifierOptionMaps,
                }
                oldModifierList = [
                    ...oldModifierList,
                    ...comboItem.oldModifierList,
                ]
                oldVariantGroupMaps = {
                    ...oldVariantGroupMaps,
                    ...comboItem.oldVariantGroupsMap,
                }
                oldItemsMap = {
                    ...oldItemsMap,
                    ...comboItem.oldItems,
                }
            }

            const discounts = buildLineDiscounts(itemLine, discountList)
            const { voidCompDiscountAmount, voidComp } = buildLineComp(itemLine)

            const voidReason = buildLineVoid(itemLine)

            const savedAssignData: AssignTableContract | undefined =
                itemLine.assignTo ?? undefined

            const oi: OrderItemViewModel = {
                id: itemLine.orderLineID!,
                item: {
                    //item need Variants and VariantGroup from baseItem
                    ...item,
                    baseItemId:
                        item?.itemId === itemLine.itemID
                            ? undefined
                            : item?.itemId,
                },
                orderID: itemLine.orderID,
                amount: itemLine.orderQuantity ?? 1,
                note: itemLine.note ?? '',
                usedVariants: usedVariants ?? {},
                usedModifiers: usedModifiers ?? {},
                usedItems: usedItems ?? {},
                discounts,
                lineTotal: round(
                    new Decimal(itemLine.subtotal ?? 0).div(
                        itemLine.orderQuantity || 1,
                    ),
                ).toNumber(),
                lineTotalWithItemDiscount:
                    (itemLine.subtotal ?? 0) -
                    (itemLine.orderLineDiscountAmount ?? 0),
                voidComp: voidComp! || voidReason!,
                lineCompDiscount: voidCompDiscountAmount,
                status: itemLine.status ?? OrderLineStatus.New,
                kdsStatus: itemLine.kdsStatus ?? OrderLineKdsStatus.Unknown,
                inventoryStatus:
                    itemLine.inventoryStatus ?? OrderInventoryStatus.None,
                kitchenPrintStatus:
                    itemLine.kitchenPrintStatus ??
                    KitchenPrintStatus.NotPrintOrUndefined,
                isSaved: true,
                menuId: itemLine.menuID!,
                menuSectionId: itemLine.menuSectionID ?? MENUSECTION_ALL_ITEMS,
                error: undefined,
                isItemUnavailable: false,
                displayName: itemLine.displayName!,
                isSharedOrderLine: itemLine.isSharedLine,
                isSubSharedOrderLine: Boolean(itemLine.sharedOrderLineID),
                sharedOrderLineID: itemLine.sharedOrderLineID!,
                hasOrderLinePaid: itemLine.hasOrderLinePaid,
                overriddenIsTaxInclusive: itemLine.isTaxInclusive,
                overriddentaxID: itemLine.taxID,
                assignTo: savedAssignData?.targetOrderIDs ?? [],
                seatNote: itemLine.seatNote,
                createdOn: dayjs(itemLine.createdOn),
                isAutoAppliedDiscount: itemLine.isAutoAppliedDiscount ?? false,
                subtotal: new Decimal(itemLine.subtotal ?? 0),
            }
            return oi
        },
    )

    return {
        orderItems: oItems,
        removedData: {
            itemsMap: oldItemsMap,
            variantGroupsMap: oldVariantGroupMaps,
            modifierGroupsMap: oldModifierGroupMaps,
            modifierOptionsMap: oldModifierOptionMaps,
            modifierList: oldModifierList,
            comboChoicesMap: oldComboMaps,
        },
    }
}

function buildLineComp(line: OrderLineLookupContract) {
    if (!line.orderLineDiscounts) return { discountAmount: 0 }
    const voidCompDiscount = line.orderLineDiscounts.find(
        (d) => d.orderDiscountType === OrderDiscountType.Comp,
    )
    if (!voidCompDiscount) return { discountAmount: 0 }

    const voidComp: VoidCompReasonModel = {
        voidCompReasonID:
            line.voidCompReasonID === null ? undefined : line.voidCompReasonID,
        type: VoidCompReasonType.Comp,
        name: line.voidCompReason,
        note: line.voidCompNotes,
        value: voidCompDiscount.discountValue ?? 0,
        isPercentage: voidCompDiscount.isPercentageDiscount ?? false,
    }

    return { voidCompDiscountAmount: voidCompDiscount.discountAmount, voidComp }
}

function buildLineVoid(line: OrderLineLookupContract) {
    const isVoid = line.status === OrderLineStatus.Voided
    if (isVoid) {
        const voidComp = {
            voidCompReasonID: line.voidCompReasonID,
            type: VoidCompReasonType.Void,
            name: line.voidCompReason,
            note: line.voidCompNotes,
            value: 0,
            isPercentage: false,
        }

        return voidComp
    }
    return undefined
}
function buildLineDiscounts(
    line: OrderLineLookupContract,
    discountList: DiscountContract[],
): DiscountContractWithCustom[] {
    return (
        line.orderLineDiscounts
            ?.filter((ld) => ld.orderDiscountType !== OrderDiscountType.Comp)
            .map<DiscountContractWithCustom>((ld, idx) => {
                const predefinedDiscount = discountList.find(
                    (d) => d.discountID === ld.discountID,
                )

                if (predefinedDiscount != null)
                    return {
                        ...predefinedDiscount,
                        isCustomDiscount: false,
                        discountRedemptionId: ld.discountRedemptionID!,
                        orderLineDiscountId: ld.orderLineDiscountID,
                    }
                else
                    return {
                        type: ld.isPercentageDiscount
                            ? DiscountType.Percentage
                            : DiscountType.FixedAmount,
                        value: ld.discountValue ?? 0,
                        isCustomDiscount: true,
                        isActive: true,
                        isCombinable: true,
                        level: DiscountLevel.Item,
                        isAutomaticDiscount: false,
                        name: ld.discountName,
                        discountID: makeCustomDiscountId(
                            line.itemID!!,
                            idx + 1,
                        ),
                        orderLineDiscountId: ld.orderLineDiscountID!,
                    }
            }) || []
    )
}
function buildEntireOrderDiscounts(
    orderDiscounts: OrderDiscountContract[],
    discounts: DiscountContract[],
): DiscountContractWithCustom[] {
    return (
        orderDiscounts
            ?.filter((od) => od.orderDiscountType !== OrderDiscountType.Comp)
            .map<DiscountContractWithCustom>((od, index) => {
                const usedDiscount = discounts.find(
                    (d) => d.discountID === od.discountID,
                )!!
                return usedDiscount
                    ? {
                          ...usedDiscount,
                          isCustomDiscount: false,
                          orderLineDiscountId: od.orderDiscountID,
                          discountRedemptionId: od.discountRedemptionID!,
                      }
                    : {
                          type: od.isPercentageDiscount
                              ? DiscountType.Percentage
                              : DiscountType.FixedAmount,
                          value: od.discountValue,
                          isCustomDiscount: true,
                          isActive: true,
                          isCombinable: true,
                          level: DiscountLevel.EntireOrder,
                          isAutomaticDiscount: false,
                          name: od.discountName!,
                          orderLineDiscountId: od.orderDiscountID,
                          discountID: `custom_discount_entire_level_${
                              index + 1
                          }`,
                      }
            }) || []
    )
}

function buildEntireVoidCompModel(
    loadedOrder: OrderDetailContract | undefined,
) {
    if (!loadedOrder) return
    let entireOrderVoidComp: VoidCompReasonModel | undefined
    const compDiscount = loadedOrder.orderDiscounts?.find(
        (d) => d.orderDiscountType === OrderDiscountType.Comp,
    )

    if (loadedOrder.isComp && compDiscount) {
        entireOrderVoidComp = {
            voidCompReasonID: loadedOrder.voidCompReasonID ?? '',
            type: VoidCompReasonType.Comp,
            name: loadedOrder.voidCompReason,
            note: loadedOrder.voidCompNotes,
            value: compDiscount.discountValue,
            isPercentage: compDiscount.isPercentageDiscount,
        }
    } else if (loadedOrder.status === OrderStatus.Voided) {
        entireOrderVoidComp = {
            voidCompReasonID: loadedOrder.voidCompReasonID ?? '',
            type: VoidCompReasonType.Void,
            name: loadedOrder.voidCompReason,
            note: loadedOrder.voidCompNotes,
        }
    }
    return entireOrderVoidComp
}
export {
    buildMockOrderLineToOrderItems,
    buildOrderItems,
    buildEntireVoidCompModel,
    buildEntireOrderDiscounts,
}
